import React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useWorkspaceLayout } from '../../context/WorkspaceLayoutContext';
import { formatShortcut, WORKSPACE_SHORTCUTS } from '../../hooks/useWorkspaceShortcuts';

/**
 * Keyboard key chip.
 * @param {{children: React.ReactNode}} props - Props.
 * @returns {JSX.Element} kbd element.
 */
export function Kbd({ children }) {
  return (
    <Box
      component="kbd"
      sx={{
        fontFamily: 'inherit',
        fontSize: 11.5,
        fontWeight: 500,
        lineHeight: '18px',
        color: 'var(--q-muted, #5E5A54)',
        bgcolor: 'var(--q-subtle, #F1EFEB)',
        border: '1px solid var(--q-border, #E3E0DA)',
        borderRadius: 'var(--q-radius-sm, 6px)',
        px: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Global keyboard shortcuts reference (opened with "?", the Help menu, the palette or Inky's
 * dock menu). Editor-specific shortcuts are listed in the editor's own dialog (Mod+/).
 * @returns {JSX.Element} Dialog.
 */
export default function ShortcutsHelpDialog() {
  const { shortcutsHelpOpen, setShortcutsHelpOpen } = useWorkspaceLayout();
  const groups = WORKSPACE_SHORTCUTS.reduce((acc, item) => {
    (acc[item.group] = acc[item.group] || []).push(item);
    return acc;
  }, {});
  const close = () => setShortcutsHelpOpen(false);

  return (
    <Dialog open={shortcutsHelpOpen} onClose={close} maxWidth="xs" fullWidth aria-labelledby="q-shortcuts-title">
      <DialogTitle id="q-shortcuts-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}>
        <Box component="span" sx={{ flex: 1 }}>Keyboard shortcuts</Box>
        <IconButton size="small" aria-label="Close" onClick={close}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {Object.entries(groups).map(([group, items]) => (
          <Box key={group} component="section" sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
            <Typography component="h3" variant="overline" sx={{ display: 'block', color: 'var(--q-muted, #5E5A54)', mb: 0.5, lineHeight: 2 }}>
              {group}
            </Typography>
            <Box component="dl" sx={{ m: 0 }}>
              {items.map((item) => (
                <Box
                  key={`${group}-${item.id}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.75, borderBottom: '1px solid var(--q-row-divider, #F0EDE8)', '&:last-of-type': { borderBottom: 0 } }}
                >
                  <Typography component="dt" variant="body2" sx={{ flex: 1, color: 'var(--q-text-2, #3D3A35)' }}>
                    {item.label}
                  </Typography>
                  <Box component="dd" sx={{ m: 0 }}>
                    <Kbd>{formatShortcut(item.keys)}</Kbd>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}
